import { useEffect, useRef, useState } from 'react'
import { Box, Button, CircularProgress, Typography } from '@mui/material'
import { shopService } from '../services'
import { readAvailableBalance, readLedgerBalance } from '../utils/checkBalanceDisplay'

const TTL_MS = 60 * 60 * 1000

function cacheKey(orgId) {
  return `sap_customer_balance_${orgId}`
}

function readCache(orgId) {
  try {
    const raw = localStorage.getItem(cacheKey(orgId))
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed?.cached_at || Date.now() - parsed.cached_at > TTL_MS) return null
    return parsed
  } catch {
    return null
  }
}

function writeCache(orgId, payload) {
  try {
    localStorage.setItem(
      cacheKey(orgId),
      JSON.stringify({ ...payload, cached_at: Date.now() }),
    )
  } catch {
    /* ignore */
  }
}

function formatMoney(value, currency = 'BDT') {
  if (value == null || Number.isNaN(value)) return '—'
  return `${currency} ${value.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`
}

export default function CheckBalanceButton({ orgId, compact = false }) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [checked, setChecked] = useState(false)
  const requestId = useRef(0)

  useEffect(() => {
    if (!orgId) return
    const cached = readCache(orgId)
    if (cached) {
      setData(cached)
      setChecked(true)
    }
  }, [orgId])

  const handleCheck = async () => {
    if (!orgId || loading) return
    const request = ++requestId.current
    setLoading(true)
    setError(null)
    try {
      const result = await shopService.getBalance(orgId)
      if (request !== requestId.current) return
      setData(result)
      setChecked(true)
      writeCache(orgId, result)
    } catch (err) {
      if (request !== requestId.current) return
      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          'Unable to load balance',
      )
    } finally {
      if (request === requestId.current) setLoading(false)
    }
  }

  if (!orgId) return null

  const currency = data?.currency || 'BDT'
  const ledger = readLedgerBalance(data)
  const available = readAvailableBalance(data)
  const ledgerUnavailable = checked && data?.customer_balance_ok === false
  const availableUnavailable = checked && data?.credit_exposure_ok === false

  return (
    <Box>
      <Button
        size={compact ? 'small' : 'medium'}
        variant="text"
        onClick={handleCheck}
        disabled={loading}
        startIcon={loading ? <CircularProgress size={14} /> : null}
        sx={{ px: compact ? 0.5 : 1, minWidth: 0 }}
      >
        {loading ? 'Checking…' : 'Check Balance'}
      </Button>
      {error ? (
        <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>
          {error}
        </Typography>
      ) : null}
      {checked && !error ? (
        <Box sx={{ mt: 1, minWidth: compact ? 160 : 200 }}>
          <Typography variant="caption" color="text.secondary" display="block">
            Ledger Balance
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {formatMoney(ledger, currency)}
          </Typography>
          {ledgerUnavailable ? (
            <Typography variant="caption" color="error" display="block">
              Ledger balance is unavailable
            </Typography>
          ) : null}
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            sx={{ mt: 0.75 }}
          >
            Available Balance
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {formatMoney(available, currency)}
          </Typography>
          {availableUnavailable ? (
            <Typography variant="caption" color="error" display="block">
              Available balance is unavailable
            </Typography>
          ) : null}
        </Box>
      ) : null}
    </Box>
  )
}
